import { useCallback } from 'react'
import { useStore } from 'react-redux'
import Uppy from '@uppy/core'

import { usesStorageEmulator } from '@/firebase/storage-routing'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { kGcsUserFilesPipelineBucketRef, kUppyUploadMaxConcurrentEmulatorUploads, kUppyUploadMaxConcurrentUploads } from '../constants'
import { initUploadBatch } from '../fetch/init-upload'
import { UppyFileUploadActions, FileUploadState } from '../store/slice'
import { selectQueuedFiles, selectProcessingFiles, selectAllFilesArray, selectIsUploading } from '../store/selectors'
import { runUppyUploadPipeline } from '../utils/run-uppy-upload-pipeline'
import { useFileUploadContext } from '../context/FileUploadContext'
import { uploadInitializedUserFileBlob } from '../utils/upload-initialized-user-file-blob'

type UseUppyUploadOptions = {
  onFileInitialized?: (params: { clientTmpId: string; serverFileId: string; storagePath: string; folderId: string }) => void
  onUploadCompleted?: (params: { clientTmpId: string; serverFileId?: string; folderId: string }) => void
  onUploadFailed?: (params: { clientTmpId: string; serverFileId?: string; folderId: string; error: string }) => void
}

type UseUppyUploadReturn = {
  uppy: Uppy
  queuedFiles: FileUploadState[]
  processingFiles: FileUploadState[]
  allFiles: FileUploadState[]
  isUploading: boolean
  startUpload: (files: FileUploadState[]) => Promise<void>
  cancelUpload: (clientTmpId: string) => void
  removeFile: (clientTmpId: string) => void
}

/**
 * Custom hook for managing file uploads with Uppy + Firebase Storage + Redux.
 *
 * Architecture:
 * - Redux is the SINGLE SOURCE OF TRUTH for file state
 * - Uppy is ONLY for temporary blob storage (drag-drop → upload complete)
 * - UI renders purely from Redux selectors
 * - No syncing of Uppy internal events to Redux (except initial file-added)
 *
 * Flow:
 * 1. Files added via Uppy drag-drop → dispatch fileQueued to Redux (handled by FileUploadProvider)
 * 2. startUpload(files) → init-upload API → selected upload transport → dispatch status to Redux
 * 3. Upload complete → dispatch uploadCompleted → central user-files poller picks it up
 * 4. Processing status updates come from central polling → dispatched back into Redux
 * 5. UI components render from selectors (queuedFiles, processingFiles)
 *
 * The Uppy instance, uploadTasksRef, and isStartingUploadRef live in FileUploadProvider
 * (mounted at MatterLayout level) so uploads survive route changes within a matter.
 */
export function useUppyUpload({ onFileInitialized, onUploadCompleted, onUploadFailed }: UseUppyUploadOptions): UseUppyUploadReturn {
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  const { uppy, uploadTasksRef, isStartingUploadRef } = useFileUploadContext()

  // All UI state comes from Redux selectors
  const queuedFiles = useAppSelector(selectQueuedFiles)
  const processingFiles = useAppSelector(selectProcessingFiles)
  const allFiles = useAppSelector(selectAllFilesArray)
  const isUploading = useAppSelector(selectIsUploading)

  // Start the upload process (with feature-specific callbacks)
  const startUpload = useCallback(
    async (files: FileUploadState[]) => {
      if (files.length === 0) return
      if (isStartingUploadRef.current) return

      isStartingUploadRef.current = true

      try {
        const storageOptions = { bucketUrl: kGcsUserFilesPipelineBucketRef }
        // Local dev uses the Storage emulator for the new pipeline. Serialize
        // uploads there so resumable finalize reliably completes for every file.
        const maxConcurrentUploads = usesStorageEmulator(storageOptions) ? kUppyUploadMaxConcurrentEmulatorUploads : kUppyUploadMaxConcurrentUploads

        await runUppyUploadPipeline({
          files,
          initUploadBatch,
          maxConcurrentUploads,
          getBlob: ({ uppyFileId }) => {
            const uppyFile = uppy.getFile(uppyFileId)
            return (uppyFile?.data as Blob | undefined) ?? null
          },
          uploadBlob: ({ clientTmpId, uploadTarget, blob, onProgress }) =>
            uploadInitializedUserFileBlob({
              clientTmpId,
              uploadTarget,
              blob,
              onProgress,
              uploadTasksRef,
            }),
          onFileInitializing: (clientTmpId) => {
            dispatch(UppyFileUploadActions.fileInitializing({ clientTmpId }))
          },
          onFileInitialized: ({ clientTmpId, serverFileId, storagePath }) => {
            dispatch(UppyFileUploadActions.fileInitialized({ clientTmpId, serverFileId, storagePath }))
            const file = files.find((item) => item.client.clientTmpId === clientTmpId)
            if (file) {
              onFileInitialized?.({
                clientTmpId,
                serverFileId,
                storagePath,
                folderId: file.client.folderId,
              })
            }
          },
          onUploadStarted: (clientTmpId) => {
            dispatch(UppyFileUploadActions.uploadStarted({ clientTmpId }))
          },
          onUploadProgress: ({ clientTmpId, progress }) => {
            dispatch(UppyFileUploadActions.uploadProgress({ clientTmpId, progress }))
          },
          onUploadCompleted: (clientTmpId) => {
            dispatch(UppyFileUploadActions.uploadCompleted({ clientTmpId }))
            const file = store.getState().uppyFileUploadState.files[clientTmpId]
            if (file) {
              onUploadCompleted?.({
                clientTmpId,
                serverFileId: file.client.serverFileId,
                folderId: file.client.folderId,
              })
            }
          },
          onUploadFailed: ({ clientTmpId, error }) => {
            dispatch(UppyFileUploadActions.uploadFailed({ clientTmpId, error }))
            const file = store.getState().uppyFileUploadState.files[clientTmpId] ?? files.find((item) => item.client.clientTmpId === clientTmpId)
            if (file) {
              onUploadFailed?.({
                clientTmpId,
                serverFileId: file.client.serverFileId,
                folderId: file.client.folderId,
                error,
              })
            }
          },
          onUploadBlobConsumed: ({ uppyFileId }) => {
            uppy.removeFile(uppyFileId)
          },
        })
      } finally {
        isStartingUploadRef.current = false
      }
    },
    [uppy, uploadTasksRef, isStartingUploadRef, dispatch, onFileInitialized, onUploadCompleted, onUploadFailed, store]
  )

  // Cancel a specific upload - dispatches directly to Redux
  const cancelUpload = useCallback(
    (clientTmpId: string) => {
      // Cancel Firebase upload task if active
      const task = uploadTasksRef.current[clientTmpId]
      if (task) {
        task.cancel()
        delete uploadTasksRef.current[clientTmpId]
      }

      // Clean up Uppy blob if still present (get fresh state to avoid stale closure)
      const file = store.getState().uppyFileUploadState.files[clientTmpId]
      if (file?.client.uppyFileId) {
        try {
          uppy.removeFile(file.client.uppyFileId)
        } catch {
          // File may already be removed from Uppy, that's fine
        }
      }

      // Remove from Redux (source of truth)
      dispatch(UppyFileUploadActions.fileRemoved({ clientTmpId }))
    },
    [store, uppy, uploadTasksRef, dispatch]
  )

  // Remove a file - dispatches directly to Redux
  const removeFile = useCallback(
    (clientTmpId: string) => {
      // Clean up Uppy blob if still present (get fresh state to avoid stale closure)
      const file = store.getState().uppyFileUploadState.files[clientTmpId]
      if (file?.client.uppyFileId) {
        try {
          uppy.removeFile(file.client.uppyFileId)
        } catch {
          // File may already be removed from Uppy, that's fine
        }
      }

      // Remove from Redux (source of truth)
      dispatch(UppyFileUploadActions.fileRemoved({ clientTmpId }))
    },
    [store, uppy, dispatch]
  )

  return {
    uppy,
    queuedFiles,
    processingFiles,
    allFiles,
    isUploading,
    startUpload,
    cancelUpload,
    removeFile,
  }
}
